import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../config/api';
import Swal from 'sweetalert2';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    code: '',
    user_type: '',
    password: '',
    confirm_password: '',
    pin: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // PIN input validation (only numbers, max 4 digits)
  const handlePinChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setFormData(prev => ({ ...prev, pin: value }));
  };

  // Validate individual input
  const validateInput = (name, value) => {
    const newErrors = { ...errors };
    
    // Clear previous error
    delete newErrors[name];

    if (!value || value.trim() === '') {
      newErrors[name] = true;
      setErrors(newErrors);
      return false;
    }

    switch (name) {
      case 'first_name':
      case 'last_name':
        if (!/^[a-zA-Z ]+$/.test(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'password':
        if (value.length < 6) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        // Check password match if confirm_password is filled
        if (formData.confirm_password && value !== formData.confirm_password) {
          newErrors.confirm_password = true;
        } else {
          delete newErrors.confirm_password;
        }
        break;

      case 'confirm_password':
        if (value !== formData.password) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'pin':
        if (!/^[0-9]{4}$/.test(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'user_type':
        if (!['qurator', 'qure'].includes(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;
    }

    setErrors(newErrors);
    return true;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate on change
    if (name === 'pin') {
      handlePinChange(e);
    } else {
      validateInput(name, value);
    }
  };

  // Check password match when either password field changes
  useEffect(() => {
    if (formData.confirm_password && formData.password !== formData.confirm_password) {
      setErrors(prev => ({ ...prev, confirm_password: true }));
    } else if (formData.confirm_password && formData.password === formData.confirm_password) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirm_password;
        return newErrors;
      });
    }
  }, [formData.password, formData.confirm_password]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate all fields
    let isValid = true;
    const newErrors = {};

    Object.keys(formData).forEach(key => {
      if (!formData[key] || formData[key].trim() === '') {
        newErrors[key] = true;
        isValid = false;
      } else if (!validateInput(key, formData[key])) {
        isValid = false;
      }
    });

    // Additional password match check
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = true;
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      setLoading(false);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please check all required fields',
        icon: 'error',
        confirmButtonColor: '#FF5722'
      });
      return;
    }

    try {
      const response = await API.post('/auth/register', formData);
      
      if (response.data.status === 'success') {
        Swal.fire({
          title: 'Success!',
          text: 'Your account has been created successfully',
          icon: 'success',
          confirmButtonColor: '#FF5722'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/login');
          }
        });
      } else {
        Swal.fire({
          title: 'Registration Failed',
          text: response.data.message || 'Registration failed. Please try again.',
          icon: 'error',
          confirmButtonColor: '#FF5722'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred. Please try again.';
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#FF5722'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        {/* Welcome Side */}
        <div className="welcome-side">
          <h2 className="h3 mb-4">Join Qure Today</h2>
          <p className="mb-4">Create your account and access powerful research tools and protocols.</p>
          
          <div className="feature-list">
            <div className="feature-item mb-3">
              <i className="bi bi-shield-check me-2"></i>
              <span>Secure Platform</span>
            </div>
            <div className="feature-item mb-3">
              <i className="bi bi-gear me-2"></i>
              <span>Advanced Tools</span>
            </div>
            <div className="feature-item mb-3">
              <i className="bi bi-people me-2"></i>
              <span>Collaborative Research</span>
            </div>
            <div className="feature-item">
              <i className="bi bi-graph-up me-2"></i>
              <span>Data Analytics</span>
            </div>
          </div>
        </div>

        {/* Form Side */}
        <div className="form-side">
          <img 
            src="https://qure.science/images/qure.webp" 
            alt="Qure" 
            className="brand-logo"
            onClick={() => navigate('/login')}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/140x60?text=Qure';
            }}
          />
          
          <h1 className="form-title">Create Account</h1>
          <p className="form-subtitle">Join our research community</p>

          <form id="registerForm" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-person"></i>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                  name="last_name"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-person"></i>
              </div>
            </div>

            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                name="username"
                placeholder="Username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <i className="bi bi-at"></i>
            </div>

            <div className="form-group">
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <i className="bi bi-envelope"></i>
            </div>

            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                  name="code"
                  placeholder="Organization Code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-building"></i>
              </div>

              <div className="form-group">
                <select
                  className={`form-select ${errors.user_type ? 'is-invalid' : ''}`}
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select User Type</option>
                  <option value="qurator">Qurator</option>
                  <option value="qure">Qure</option>
                </select>
                <i className="bi bi-person-badge"></i>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <input
                  type="password"
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-lock"></i>
              </div>

              <div className="form-group">
                <input
                  type="password"
                  className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                  name="confirm_password"
                  placeholder="Confirm Password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-lock-fill"></i>
              </div>
            </div>

            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.pin ? 'is-invalid' : ''}`}
                name="pin"
                placeholder="4-Digit PIN"
                maxLength={4}
                value={formData.pin}
                onChange={handlePinChange}
                required
                disabled={loading}
              />
              <i className="bi bi-shield-lock"></i>
            </div>

            <div className="d-grid gap-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus me-2"></i>
                    Create Account
                  </>
                )}
              </button>
              <Link to="/login" className="btn btn-outline-secondary">
                Already have an account? Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
